import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { getTokens } from "../utils/auth";
import {
  createAlbumApiAdminAlbumsPost,
  listAlbumsApiAdminAlbumsGet,
  getAlbumApiAdminAlbumsAlbumIdGet,
  updateAlbumApiAdminAlbumsAlbumIdPatch,
  deleteAlbumApiAdminAlbumsAlbumIdDelete,
} from "../api/generated";
import type {
  AlbumResponse,
  AlbumListResponse,
  AlbumCreate,
  AlbumUpdate,
  ListAlbumsApiAdminAlbumsGetResponse,
  GetAlbumApiAdminAlbumsAlbumIdGetResponse,
} from "../api/generated";

export interface AlbumFilters {
  page?: number;
  page_size?: number;
  is_public?: boolean;
  is_featured?: boolean;
  search?: string;
}

// Re-export types for convenience
export type Album = AlbumResponse;
export type { AlbumListResponse, AlbumCreate, AlbumUpdate };

/**
 * Hook for fetching paginated and filtered list of albums.
 */
export function useAlbums(filters: AlbumFilters = {}) {
  const isDevelopment = process.env.NODE_ENV === "development";
  const requiresAuth = () => {
    // In development mode, don't require auth
    if (isDevelopment) return false;
    // In production, require auth token
    const { accessToken } = getTokens();
    return !accessToken;
  };

  return useQuery<ListAlbumsApiAdminAlbumsGetResponse>({
    queryKey: ["albums", filters],
    queryFn: async () => {
      const response = await listAlbumsApiAdminAlbumsGet({
        query: {
          page: filters.page || 1,
          page_size: filters.page_size || 20,
          is_public: filters.is_public,
          is_featured: filters.is_featured,
          search: filters.search,
        },
      });

      if (!response.data) {
        throw new Error("Failed to fetch albums");
      }

      return response.data;
    },
    enabled: !requiresAuth(),
  });
}

/**
 * Hook for fetching a single album by ID.
 */
export function useAlbum(albumId: string | null) {
  const isDevelopment = process.env.NODE_ENV === "development";
  const requiresAuth = () => {
    // In development mode, don't require auth
    if (isDevelopment) return false;
    // In production, require auth token
    const { accessToken } = getTokens();
    return !accessToken;
  };

  return useQuery<GetAlbumApiAdminAlbumsAlbumIdGetResponse>({
    queryKey: ["album", albumId],
    queryFn: async () => {
      if (!albumId) {
        throw new Error("Album ID is required");
      }

      const response = await getAlbumApiAdminAlbumsAlbumIdGet({
        path: { album_id: albumId },
      });

      if (!response.data) {
        throw new Error("Failed to fetch album");
      }

      return response.data;
    },
    enabled: !!albumId && !requiresAuth(),
  });
}

/**
 * Hook for creating a new album.
 */
export function useCreateAlbum() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (data: AlbumCreate) => {
      const response = await createAlbumApiAdminAlbumsPost({
        body: data,
      });

      if (!response.data) {
        throw new Error("Failed to create album");
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["albums"] });
      queryClient.invalidateQueries({ queryKey: ["album"] });
      queryClient.invalidateQueries({ queryKey: ["galleries"] });
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
      enqueueSnackbar("Album created successfully", { variant: "success" });
    },
    onError: (error) => {
      enqueueSnackbar(
        error instanceof Error ? error.message : "Failed to create album",
        { variant: "error" }
      );
    },
  });
}

/**
 * Hook for updating an album.
 */
export function useUpdateAlbum() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      albumId,
      data,
    }: {
      albumId: string;
      data: AlbumUpdate;
    }) => {
      const response = await updateAlbumApiAdminAlbumsAlbumIdPatch({
        path: { album_id: albumId },
        body: data,
      });

      if (!response.data) {
        throw new Error("Failed to update album");
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["albums"] });
      queryClient.invalidateQueries({ queryKey: ["album"] });
      enqueueSnackbar("Album updated successfully", { variant: "success" });
    },
    onError: (error) => {
      enqueueSnackbar(
        error instanceof Error ? error.message : "Failed to update album",
        { variant: "error" }
      );
    },
  });
}

/**
 * Hook for deleting an album.
 */
export function useDeleteAlbum() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (albumId: string) => {
      const response = await deleteAlbumApiAdminAlbumsAlbumIdDelete({
        path: { album_id: albumId },
      });

      if (response.error) {
        throw new Error("Failed to delete album");
      }
    },
    onMutate: async (albumId) => {
      await queryClient.cancelQueries({ queryKey: ["albums"] });

      const previousAlbums = queryClient.getQueryData<AlbumListResponse>([
        "albums",
      ]);

      queryClient.setQueriesData<AlbumListResponse>(
        { queryKey: ["albums"] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter((album) => album.id !== albumId),
            total: old.total - 1,
          };
        }
      );

      return { previousAlbums };
    },
    onSuccess: () => {
      enqueueSnackbar("Album deleted successfully", { variant: "success" });
    },
    onError: (_err, _albumId, context) => {
      if (context?.previousAlbums) {
        queryClient.setQueryData(["albums"], context.previousAlbums);
      }
      enqueueSnackbar("Failed to delete album", { variant: "error" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["albums"] });
      queryClient.invalidateQueries({ queryKey: ["album"] });
      queryClient.invalidateQueries({ queryKey: ["galleries"] });
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
    },
  });
}
